export type Theme = "light" | "dark";

export const THEME_STORAGE_KEY = "kanban.theme";

const THEME_COLORS: Record<Theme, string> = {
  light: "#EDEDEA",
  dark: "#15191A",
};

let activeTheme: Theme = "light";
let explicitPreference = false;
let initialized = false;

export function isTheme(value: string | null): value is Theme {
  return value === "light" || value === "dark";
}

export function resolveTheme(stored: string | null, prefersDark: boolean): Theme {
  return isTheme(stored) ? stored : prefersDark ? "dark" : "light";
}

export function nextTheme(theme: Theme): Theme {
  return theme === "dark" ? "light" : "dark";
}

function readStoredTheme(): Theme | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : null;
  } catch {
    return null;
  }
}

function applyTheme(theme: Theme): Theme {
  const changed = activeTheme !== theme || document.documentElement.dataset.theme !== theme;
  activeTheme = theme;
  document.documentElement.dataset.theme = theme;

  const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (themeColor) themeColor.content = THEME_COLORS[theme];
  const colorScheme = document.querySelector<HTMLMetaElement>('meta[name="color-scheme"]');
  if (colorScheme) colorScheme.content = theme;
  const statusBar = document.querySelector<HTMLMetaElement>(
    'meta[name="apple-mobile-web-app-status-bar-style"]',
  );
  if (statusBar) statusBar.content = theme === "dark" ? "black-translucent" : "default";

  if (changed) window.dispatchEvent(new CustomEvent("kanban:themechange", { detail: theme }));
  return theme;
}

export function currentTheme(): Theme {
  return activeTheme;
}

export function setTheme(theme: Theme, { persist = false }: { persist?: boolean } = {}): Theme {
  explicitPreference = true;
  if (persist) {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // The current page still honors the selection when storage is unavailable.
    }
  }
  return applyTheme(theme);
}

export function toggleTheme(): Theme {
  return setTheme(nextTheme(activeTheme), { persist: true });
}

export function initializeTheme(): Theme {
  if (initialized || typeof window === "undefined" || typeof document === "undefined") {
    return activeTheme;
  }
  initialized = true;

  const preference = window.matchMedia("(prefers-color-scheme: dark)");
  const stored = readStoredTheme();
  explicitPreference = stored !== null;
  applyTheme(resolveTheme(stored, preference.matches));
  const followSystemPreference = (event: MediaQueryListEvent) => {
    if (!explicitPreference) applyTheme(event.matches ? "dark" : "light");
  };
  if (typeof preference.addEventListener === "function") {
    preference.addEventListener("change", followSystemPreference);
  } else {
    preference.addListener(followSystemPreference);
  }
  return activeTheme;
}

if (typeof window !== "undefined" && typeof document !== "undefined") initializeTheme();
